import { faCalendar, faXmark } from "@fortawesome/pro-regular-svg-icons";
import { format } from "date-fns";
import { twJoin } from "tailwind-merge";

import {
  DateTimePicker,
  DateTimePickerProps,
} from "src/base-components/DateTimePicker";
import { Icon } from "src/base-components/Icon";
import { CustomPopover as Popover } from "src/base-components/Popover";
import { useIsInFloatingWindow } from "src/datasets/DatasetTable/FloatingWindow";

export type DateTimePickerDropdownProps = {
  placeholder?: string;
  borderless?: boolean;
  errored?: boolean;
  onReset?: () => void;
} & DateTimePickerProps;

export const DateTimePickerDropdown = ({
  disabled = false,
  value,
  placeholder = "Pick a date and time",
  borderless = false,
  errored = false,
  dateOnly = false,
  onReset,
  ...props
}: DateTimePickerDropdownProps) => {
  const isInFloatingWindow = useIsInFloatingWindow();
  const formatString = dateOnly ? "MMM dd, yyyy" : "MMM dd, yyyy hh:mm a";

  const button = (
    <div
      className={twJoin(
        "flex h-8 w-full items-center gap-x-1 whitespace-nowrap rounded-lg px-2 py-1 text-left font-inter-normal-12px text-gray-800 focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-500/25",
        !borderless && "border border-gray-200",
        errored && !borderless && "border-red-600 ring-2 ring-red-400/25",
        disabled && "cursor-not-allowed bg-gray-50 text-gray-500",
      )}
      data-testid="date-time-picker-button"
      tabIndex={disabled ? -1 : 0}
    >
      <Icon
        color={disabled ? "text-gray-400" : "text-gray-500"}
        icon={faCalendar}
        size="xs"
      />
      <div
        className={twJoin(
          "flex-1",
          disabled && "text-gray-400",
          !disabled && !value && "text-gray-500",
        )}
      >
        {value ? format(value, formatString) : <span>{placeholder}</span>}
      </div>
      {onReset && value && !disabled && (
        <Icon
          color="text-gray-500 hover:text-gray-800"
          icon={faXmark}
          size="xs"
          onClick={(e) => {
            e.stopPropagation();
            onReset();
          }}
        />
      )}
    </div>
  );

  return (
    <div className={twJoin("w-full", disabled && "pointer-events-none")}>
      <Popover
        button={button}
        className={isInFloatingWindow ? "z-[60]" : "z-10"}
        dataLoc="date-time-picker"
        placement="bottom"
      >
        <DateTimePicker
          dateOnly={dateOnly}
          disabled={disabled}
          value={value}
          {...props}
        />
      </Popover>
    </div>
  );
};
